import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, IconButton, LinearProgress, Tooltip } from '@mui/material';
import {
  Add,
  CloseFullscreen,
  CorporateFare,
  InfoOutlined,
  OpenInFull,
  UnfoldLess,
  UnfoldMore,
} from '@mui/icons-material';
import { useAppTranslation } from '../../services/i18n';
import { DropInstruction, WorkplaceRow } from '../../helpers/workplaceTree';
import { WORKPLACE_ROW_HEIGHT, WorkplaceTreeRow } from './WorkplaceTreeRow';
import { WorkplaceShortcuts } from './WorkplaceShortcuts';

/** rows kept rendered above and below the viewport, so keyboard paging stays smooth */
const OVERSCAN = 6;
const TYPEAHEAD_TIMEOUT = 800;
const CONTROL_SELECTOR = 'button, [role="button"], a, input, select, textarea';
/** how long a collapsed branch has to be hovered before it opens itself */
const AUTO_EXPAND_DELAY = 600;
/** how close to the edge of the list a drag has to get before the list follows along */
const AUTO_SCROLL_ZONE = 56;
/** pixels per frame right at the edge; it eases in from zero across the whole zone */
const AUTO_SCROLL_MAX_SPEED = 20;

const autoScrollSpeed = (depth: number): number =>
  Math.ceil((Math.min(depth, AUTO_SCROLL_ZONE) / AUTO_SCROLL_ZONE) * AUTO_SCROLL_MAX_SPEED);
/** keys a focused button or field consumes itself */
const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar', 'F2', 'Delete', 'Backspace'];

interface Props {
  rows: WorkplaceRow[];
  selectedId: number | null;
  isFilterActive: boolean;
  /** the filter result is still on its way, the rows below are stale */
  isFiltering: boolean;
  /** the last filter request failed, so the rows below are not filtered at all */
  filterError: boolean;
  isFullscreen: boolean;
  areAllExpanded: boolean;
  /** false when the whole structure is flat and there is nothing to expand */
  canToggleAll: boolean;
  onToggle: (id: number) => void;
  onToggleAll: () => void;
  onSelect: (id: number) => void;
  onEdit: (id: number) => void;
  onToggleFullscreen: () => void;
  onCreateRoot: () => void;
  onCreateSibling: (id: number) => void;
  onCreateChild: (id: number) => void;
  /** false for a structure with no children anywhere -- then dragging only reorders */
  canNest: boolean;
  /** off draws a flat list: no indentation, no chevrons, no nesting on offer */
  isTreeEnabled: boolean;
  /**
   * The organization has no workplaces at all -- which is a different silence from
   * a filter that matched nothing, and the only one worth answering with a way in.
   */
  isEmpty: boolean;
  /** the dragged unit and its own subtree, refused as drop targets */
  forbiddenDropIds: Set<number> | null;
  onDragStart: (id: number) => void;
  onDragEnd: () => void;
  onDrop: (draggedId: number, targetId: number, instruction: DropInstruction) => void;
  /** -1 moves the unit up among its siblings, 1 moves it down */
  onReorder: (id: number, delta: -1 | 1) => void;
  /** opens the picker for a parent that is neither a sibling nor on screen */
  onMoveTo: (id: number) => void;
  onIndent: (id: number) => void;
  onOutdent: (id: number) => void;
  onDelete: (id: number) => void;
  onDeselect: () => void;
  /** bumped by the parent whenever the keyboard should return to the tree */
  focusToken: number;
}

const normalize = (value: string): string =>
  value.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export const WorkplaceTree: React.FC<Props> = ({
  rows,
  selectedId,
  isFilterActive,
  isFiltering,
  filterError,
  isFullscreen,
  areAllExpanded,
  canToggleAll,
  canNest,
  isTreeEnabled,
  isEmpty,
  forbiddenDropIds,
  onDragStart,
  onDragEnd,
  onDrop,
  onToggle,
  onToggleAll,
  onSelect,
  onEdit,
  onToggleFullscreen,
  onCreateRoot,
  onCreateSibling,
  onCreateChild,
  onReorder,
  onMoveTo,
  onIndent,
  onOutdent,
  onDelete,
  onDeselect,
  focusToken,
}) => {
  const t = useAppTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [activeId, setActiveId] = useState<number | null>(null);
  const shouldFocus = useRef(false);
  const typeahead = useRef({ buffer: '', at: 0 });
  const [shortcutsAnchor, setShortcutsAnchor] = useState<HTMLElement | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    id: number;
    instruction: DropInstruction;
  } | null>(null);

  const handleDragOver = useCallback((id: number, instruction: DropInstruction | null) => {
    setDropTarget(instruction ? { id, instruction } : null);
  }, []);

  /** the running drag: where the cursor is, the scrolling frame, and how to let go again */
  const dragState = useRef<{ x: number; y: number; frame: number; release?: () => void }>({
    x: 0,
    y: 0,
    frame: 0,
  });

  const endDrag = useCallback(() => {
    const state = dragState.current;
    if (!state.frame) {
      return;
    }
    cancelAnimationFrame(state.frame);
    state.frame = 0;
    state.release?.();
    state.release = undefined;
    setDropTarget(null);
    onDragEnd();
  }, [onDragEnd]);

  const beginDrag = useCallback(
    (id: number, x: number, y: number) => {
      const state = dragState.current;
      if (state.frame) {
        return;
      }
      state.x = x;
      state.y = y;
      onDragStart(id);

      const track = (event: DragEvent) => {
        state.x = event.clientX;
        state.y = event.clientY;
      };
      // virtualisation can unmount the dragged row mid-drag, and a detached element's
      // dragend never reaches the list -- the window hears the end of the drag either way
      window.addEventListener('dragover', track);
      window.addEventListener('dragend', endDrag);
      window.addEventListener('drop', endDrag);
      state.release = () => {
        window.removeEventListener('dragover', track);
        window.removeEventListener('dragend', endDrag);
        window.removeEventListener('drop', endDrag);
      };

      // dragover stops firing once the cursor rests, so the pull runs on its own frames
      const step = () => {
        const element = scrollRef.current;
        if (!element) {
          return;
        }
        const rect = element.getBoundingClientRect();
        // a cursor that has left the list sideways is aiming at something else entirely
        const aside = state.x < rect.left || state.x > rect.right;
        const above = AUTO_SCROLL_ZONE - (state.y - rect.top);
        const below = AUTO_SCROLL_ZONE - (rect.bottom - state.y);
        const delta = aside
          ? 0
          : above > 0
          ? -autoScrollSpeed(above)
          : below > 0
          ? autoScrollSpeed(below)
          : 0;
        if (delta) {
          // the scroll event this triggers keeps the rendered window in step
          element.scrollTop += delta;
        }
        state.frame = requestAnimationFrame(step);
      };
      state.frame = requestAnimationFrame(step);
    },
    [endDrag, onDragStart]
  );

  useEffect(
    () => () => {
      const state = dragState.current;
      if (state.frame) {
        cancelAnimationFrame(state.frame);
      }
      state.release?.();
    },
    []
  );

  // a collapsed branch the cursor rests on opens itself, so its children can be aimed at
  useEffect(() => {
    if (!dropTarget || dropTarget.instruction !== 'inside') {
      return;
    }
    const target = rows.find((row) => row.node.id === dropTarget.id);
    if (!target?.hasChildren || target.isExpanded) {
      return;
    }
    const timer = window.setTimeout(() => onToggle(dropTarget.id), AUTO_EXPAND_DELAY);
    return () => window.clearTimeout(timer);
  }, [dropTarget, rows, onToggle]);

  const activeIndex = useMemo(() => {
    const index = rows.findIndex((row) => row.node.id === activeId);
    return index >= 0 ? index : 0;
  }, [rows, activeId]);

  // the roving tabindex must always land on a row that actually exists
  useEffect(() => {
    if (!rows.length) {
      setActiveId(null);
    } else if (!rows.some((row) => row.node.id === activeId)) {
      setActiveId(rows[0].node.id);
    }
  }, [rows, activeId]);

  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const measure = () => setViewportHeight(element.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  // a filter can shrink the list below the current offset, which would render an empty window
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    const maxScroll = Math.max(0, rows.length * WORKPLACE_ROW_HEIGHT - element.clientHeight);
    if (element.scrollTop > maxScroll) {
      element.scrollTop = maxScroll;
    }
    // the browser clamps its own scrollTop silently when the content shrinks, so the
    // state has to be re-read rather than only written -- otherwise the window is
    // computed from an offset that no longer exists and renders nothing
    setScrollTop(Math.min(element.scrollTop, maxScroll));
  }, [rows.length]);

  const keyHandler = useRef<(event: KeyboardEvent) => void>();
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }
    const listener = (event: KeyboardEvent) => keyHandler.current?.(event);
    panel.addEventListener('keydown', listener, true);
    return () => panel.removeEventListener('keydown', listener, true);
  }, []);

  const focusActiveRow = useCallback(() => {
    // after a delete the active row is gone, so fall back to whatever is on top
    const element =
      scrollRef.current?.querySelector<HTMLElement>('[data-workplace-id="' + activeId + '"]') ||
      scrollRef.current?.querySelector<HTMLElement>('[role="treeitem"]');
    element?.focus();
  }, [activeId]);

  // the parent asks for the focus back after saving, creating or deleting
  useEffect(() => {
    if (focusToken > 0) {
      focusActiveRow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusToken]);

  // focus follows the active row only when the move came from the keyboard
  useEffect(() => {
    if (!shouldFocus.current || activeId === null) {
      return;
    }
    const element = scrollRef.current?.querySelector<HTMLElement>(
      '[data-workplace-id="' + activeId + '"]'
    );
    if (element) {
      element.focus();
      shouldFocus.current = false;
    }
  });

  const moveTo = useCallback(
    (index: number) => {
      if (!rows.length) {
        return;
      }
      const target = Math.max(0, Math.min(rows.length - 1, index));
      const element = scrollRef.current;
      if (element) {
        const top = target * WORKPLACE_ROW_HEIGHT;
        if (top < element.scrollTop) {
          element.scrollTop = top;
        } else if (top + WORKPLACE_ROW_HEIGHT > element.scrollTop + element.clientHeight) {
          element.scrollTop = top + WORKPLACE_ROW_HEIGHT - element.clientHeight;
        }
        setScrollTop(element.scrollTop);
      }
      shouldFocus.current = true;
      setActiveId(rows[target].node.id);
      // selection follows the keyboard like it follows a click; an open form keeps its
      // workplace anyway, because the editor refuses to switch under it
      onSelect(rows[target].node.id);
    },
    [rows, onSelect]
  );

  const parentIndexOf = useCallback(
    (index: number): number => {
      const level = rows[index]?.level ?? 0;
      for (let i = index - 1; i >= 0; i--) {
        if (rows[i].level < level) {
          return i;
        }
      }
      return -1;
    },
    [rows]
  );

  const runTypeahead = useCallback(
    (key: string, fromIndex: number) => {
      const now = Date.now();
      const state = typeahead.current;
      state.buffer = now - state.at > TYPEAHEAD_TIMEOUT ? key : state.buffer + key;
      state.at = now;
      const needle = normalize(state.buffer);
      for (let offset = 1; offset <= rows.length; offset++) {
        const index = (fromIndex + offset) % rows.length;
        const unit = rows[index].node.unit;
        if (
          normalize(unit.name).startsWith(needle) ||
          normalize(unit.code || '').startsWith(needle)
        ) {
          moveTo(index);
          return;
        }
      }
    },
    [rows, moveTo]
  );

  const handleKeyDown = (event: KeyboardEvent | React.KeyboardEvent) => {
    // act on the row that really holds the focus; the roving state can lag behind
    // when something else moved the focus for us
    const focusedRow = (event.target as HTMLElement).closest?.('[data-workplace-id]');
    // Navigation keys drive the tree from anywhere in the panel, but a focused control
    // keeps the keys that activate it -- the row menu button sits inside a row, and this
    // listener runs in the capture phase, so the button cannot stop the event itself.
    if (
      ACTIVATION_KEYS.includes(event.key) &&
      (event.target as HTMLElement).closest?.(CONTROL_SELECTOR)
    ) {
      return;
    }
    const focusedId = Number(focusedRow?.getAttribute('data-workplace-id'));
    const focusedIndex = rows.findIndex((item) => item.node.id === focusedId);
    const index = focusedIndex >= 0 ? focusedIndex : activeIndex;
    const row = rows[index];
    if (!row) {
      return;
    }
    if (focusedIndex >= 0 && row.node.id !== activeId) {
      setActiveId(row.node.id);
    }
    // collapsing is disabled while a filter forces the tree open
    const canToggle = !isFilterActive;
    // Ctrl on Windows and Linux, Cmd on Apple keyboards -- both are accepted
    const modifier = event.ctrlKey || event.metaKey;

    if (event.altKey) {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          onReorder(row.node.id, -1);
          return;
        case 'ArrowDown':
          event.preventDefault();
          onReorder(row.node.id, 1);
          return;
        // a flat list has nothing to nest into, so these keys stay inert there
        case 'ArrowRight':
          event.preventDefault();
          if (isTreeEnabled) {
            onIndent(row.node.id);
          }
          return;
        case 'ArrowLeft':
          event.preventDefault();
          if (isTreeEnabled) {
            onOutdent(row.node.id);
          }
          return;
        default:
          return;
      }
    }

    if (modifier && event.key === 'Enter') {
      event.preventDefault();
      if (event.shiftKey && isTreeEnabled) {
        onCreateChild(row.node.id);
      } else {
        onCreateSibling(row.node.id);
      }
      return;
    }

    switch (event.key) {
      case 'Delete':
      case 'Backspace':
        event.preventDefault();
        onDelete(row.node.id);
        return;
      case 'ArrowDown':
        event.preventDefault();
        moveTo(index + 1);
        return;
      case 'ArrowUp':
        event.preventDefault();
        moveTo(index - 1);
        return;
      case 'Home':
        event.preventDefault();
        moveTo(0);
        return;
      case 'End':
        event.preventDefault();
        moveTo(rows.length - 1);
        return;
      case 'ArrowRight':
        event.preventDefault();
        if (row.hasChildren && !row.isExpanded && canToggle) {
          onToggle(row.node.id);
        } else if (row.hasChildren && row.isExpanded) {
          moveTo(index + 1);
        }
        return;
      case 'ArrowLeft': {
        event.preventDefault();
        if (row.hasChildren && row.isExpanded && canToggle) {
          onToggle(row.node.id);
          return;
        }
        const parentIndex = parentIndexOf(index);
        if (parentIndex >= 0) {
          moveTo(parentIndex);
        }
        return;
      }
      case ' ':
        event.preventDefault();
        onSelect(row.node.id);
        return;
      case 'Enter':
      case 'F2':
        event.preventDefault();
        onEdit(row.node.id);
        return;
      case 'Escape':
        event.preventDefault();
        onDeselect();
        return;
      default:
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          runTypeahead(event.key, index);
        }
    }
  };

  keyHandler.current = handleKeyDown;

  // the rows carry the ancestors of the matches too, and those are not matches themselves
  const matchCount = rows.reduce((count, row) => count + (row.isMatch ? 1 : 0), 0);

  const firstVisible = Math.max(0, Math.floor(scrollTop / WORKPLACE_ROW_HEIGHT) - OVERSCAN);
  const lastVisible = Math.min(
    rows.length,
    Math.ceil((scrollTop + (viewportHeight || 600)) / WORKPLACE_ROW_HEIGHT) + OVERSCAN
  );
  const visibleRows = rows
    .slice(firstVisible, lastVisible)
    .map((row, index) => ({ row, index: firstVisible + index }));
  // the focused row must stay mounted even when scrolled out of view, or focus would be lost
  const renderedRows =
    activeIndex < firstVisible || activeIndex >= lastVisible
      ? [{ row: rows[activeIndex], index: activeIndex }, ...visibleRows].filter((item) => item.row)
      : visibleRows;

  return (
    <div className={'workplace-panel workplace-panel--tree'} ref={panelRef}>
      <div className={'workplace-panel__head workplace-panel__head--dark'}>
        <h3 className={'workplace-panel__title'}>
          {t(isTreeEnabled ? 'workplaces.tree.title' : 'workplaces.tree.flatTitle')}
        </h3>
        <IconButton
          size={'small'}
          aria-label={t('workplaces.shortcuts.title')}
          aria-expanded={!!shortcutsAnchor}
          onClick={(event) => setShortcutsAnchor(event.currentTarget)}
        >
          <InfoOutlined fontSize={'small'} className={'workplace-panel__help'} />
        </IconButton>
        <WorkplaceShortcuts
          anchorEl={shortcutsAnchor}
          isTreeEnabled={isTreeEnabled}
          onClose={() => setShortcutsAnchor(null)}
        />
        <span className={'workplace-panel__spacer'} />
        {/* a flat structure has nothing to expand, so the button is not offered at all */}
        {canToggleAll && (
          <Tooltip
            title={
              isFilterActive
                ? t('workplaces.tree.expandAllDisabled')
                : areAllExpanded
                ? t('workplaces.tree.collapseAll')
                : t('workplaces.tree.expandAll')
            }
          >
            {/* the tooltip needs a mountable child even while the button is inert */}
            <span>
              <IconButton
                size={'small'}
                className={'workplace-panel__action'}
                disabled={isFilterActive}
                aria-label={
                  areAllExpanded
                    ? t('workplaces.tree.collapseAll')
                    : t('workplaces.tree.expandAll')
                }
                onClick={onToggleAll}
              >
                {areAllExpanded ? (
                  <UnfoldLess fontSize={'small'} />
                ) : (
                  <UnfoldMore fontSize={'small'} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
        <IconButton
          size={'small'}
          className={'workplace-panel__action'}
          aria-expanded={isFullscreen}
          aria-label={
            isFullscreen ? t('workplaces.tree.exitFullscreen') : t('workplaces.tree.fullscreen')
          }
          onClick={onToggleFullscreen}
        >
          {isFullscreen ? (
            <CloseFullscreen fontSize={'small'} />
          ) : (
            <OpenInFull fontSize={'small'} />
          )}
        </IconButton>
      </div>

      {/* the filter round-trip is noticeable, so the tree says out loud that it is working */}
      {isFiltering && <LinearProgress className={'workplace-panel__progress'} />}
      <div
        aria-live={'polite'}
        className={
          'workplace-panel__status' + (filterError ? ' workplace-panel__status--error' : '')
        }
        aria-busy={isFiltering}
      >
        {isFiltering
          ? t('workplaces.tree.filtering')
          : filterError
          ? t('workplaces.tree.filterFailed')
          : isFilterActive
          ? t('workplaces.tree.filterResult', { n: matchCount })
          : ''}
      </div>

      <div
        ref={scrollRef}
        onDragStartCapture={(event) => {
          const row = (event.target as HTMLElement).closest?.('[data-workplace-id]');
          const id = Number(row?.getAttribute('data-workplace-id'));
          if (id) {
            beginDrag(id, event.clientX, event.clientY);
          }
        }}
        className={'workplace-panel__body' + (isFiltering ? ' workplace-panel__body--busy' : '')}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        onMouseDown={(event) => {
          // a click next to the rows would otherwise leave the tree without focus
          if (!(event.target as HTMLElement).closest('[role="treeitem"]')) {
            window.setTimeout(focusActiveRow, 0);
          }
        }}
      >
        {rows.length === 0 && !isFiltering ? (
          isEmpty ? (
            <div className={'workplace-empty'}>
              <CorporateFare className={'workplace-empty__icon'} />
              <h4 className={'workplace-empty__title'}>{t('workplaces.emptyState.title')}</h4>
              <p className={'workplace-empty__lead'}>
                {t(isTreeEnabled ? 'workplaces.emptyState.lead' : 'workplaces.emptyState.leadFlat')}
              </p>
              <Button
                variant={'contained'}
                color={'primary'}
                startIcon={<Add />}
                onClick={onCreateRoot}
              >
                {t(
                  isTreeEnabled ? 'workplaces.emptyState.addRoot' : 'workplaces.emptyState.addFlat'
                )}
              </Button>
            </div>
          ) : (
            <p className={'workplace-panel__empty'}>{t('workplaces.tree.noMatch')}</p>
          )
        ) : (
          <div
            role={'tree'}
            aria-label={t(isTreeEnabled ? 'workplaces.tree.title' : 'workplaces.tree.flatTitle')}
            aria-multiselectable={false}
            aria-busy={isFiltering}
            className={'workplace-tree'}
            style={{ height: rows.length * WORKPLACE_ROW_HEIGHT }}
          >
            {renderedRows.map(({ row, index }) => (
              <WorkplaceTreeRow
                key={row.node.id}
                row={row}
                top={index * WORKPLACE_ROW_HEIGHT}
                isActive={row.node.id === (activeId ?? rows[0]?.node.id)}
                isSelected={row.node.id === selectedId}
                canToggle={!isFilterActive}
                canDrag={!isFilterActive}
                canNest={canNest}
                isTreeEnabled={isTreeEnabled}
                forbiddenIds={forbiddenDropIds}
                dropInstruction={
                  dropTarget?.id === row.node.id ? dropTarget.instruction : null
                }
                onDragOver={handleDragOver}
                onDrop={(draggedId, targetId, instruction) => {
                  setDropTarget(null);
                  onDrop(draggedId, targetId, instruction);
                }}
                onToggle={onToggle}
                onSelect={(id) => {
                  shouldFocus.current = false;
                  setActiveId(id);
                  onSelect(id);
                }}
                onEdit={onEdit}
                onCreateChild={onCreateChild}
                onCreateSibling={onCreateSibling}
                onMoveTo={onMoveTo}
                onReorder={onReorder}
                onDelete={onDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* the empty state carries the way in itself, so the footer would only say it twice */}
      {!isEmpty && (
        <div className={'workplace-panel__foot workplace-panel__foot--right'}>
          <Button size={'small'} startIcon={<Add />} onClick={onCreateRoot}>
            {t(isTreeEnabled ? 'workplaces.tree.addRoot' : 'workplaces.tree.addFlat')}
          </Button>
        </div>
      )}
    </div>
  );
};
